import React from "react";
import {
  CdpIndustryHero,
  CdpIndustriesCustomerLogos,
  CdpIndustryBusinessOutcomes,
  CdpIndustryLeveragingDataStats,
  CdpIndustryPersonalizationLeadersCards,
  CdpIndustryUseCases,
  CdpIndustryConsiderationsTable,
  CdpIndustryWhyHightouch,
  CdpIndustryResources,
} from "components/cdp-industries";
import { DestinationMarquee } from "components/content";
import { Page } from "components/page";
import { sectionSpacingY } from "components/section";
import { type CdpIndustrySlug, cdpIndustryVariants, CdpIndustryProvider } from "contexts/cdp-industry-context";
import {
  type Banner,
  type Customer,
  type IntegrationSimple,
  getAnnouncementBanner,
  getStackedLogos,
  getSimpleIntegrations,
  getPage,
  type Page as SanityPage,
  type BlogSimple,
  getFeaturedBlogs,
} from "lib/sanity";
import { type GetStaticProps, type GetStaticPaths, type NextPage } from "next";
import { startCase } from "lodash";

export type CdpIndustryPageProps = Readonly<{
  industrySlug: CdpIndustrySlug;
  announcementBanner?: Banner;
  // Next can't actually seralize `undefined` in the page props
  // eslint-disable-next-line @typescript-eslint/ban-types
  industrySolutionPage: SanityPage | null;
  stackedLogos: Customer[][];
  destinations: IntegrationSimple[];
  referenceBlogPosts: BlogSimple[];
}>;

function getMetaTitle(name: string) {
  return `The ${name} CDP Playbook: Hidden Strategies & Secrets`;
}

function getMetaDescription(pluralName: string) {
  return `Learn how ${pluralName} are using CDPs to drive growth, build loyalty, increase revenue, and stay ahead of the competition.`;
}

const CdpIndustryPageTemplate: NextPage<CdpIndustryPageProps> = ({
  industrySlug,
  announcementBanner,
  industrySolutionPage,
  stackedLogos,
  destinations,
  referenceBlogPosts,
}) => {
  const industry = cdpIndustryVariants[industrySlug];

  return (
    <Page
      announcementBanner={announcementBanner}
      title={getMetaTitle(industry.titleCaseName ?? startCase(industry.name))}
      description={getMetaDescription(industry.pluralNoun)}
      bgColor={`new.${industry.color}.150`}
      footerOptions={{ variant: industry.color }}
    >
      <CdpIndustryProvider industry={industry}>
        <CdpIndustryHero />
        <CdpIndustriesCustomerLogos
          industrySolutionPage={industrySolutionPage ?? undefined}
          stackedLogos={stackedLogos}
        />
        <CdpIndustryBusinessOutcomes />
        <CdpIndustryLeveragingDataStats />
        <CdpIndustryPersonalizationLeadersCards />
        <CdpIndustryUseCases />
        <CdpIndustryConsiderationsTable />
        <CdpIndustryWhyHightouch />
        <CdpIndustryResources blogPosts={referenceBlogPosts} />
        <DestinationMarquee destinations={destinations} my="0" pb={sectionSpacingY} />
      </CdpIndustryProvider>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<CdpIndustryPageProps> = async ({ params }) => {
  const slug = params?.slug ? (String(params?.slug) as CdpIndustrySlug) : undefined;
  const industry = slug ? cdpIndustryVariants[slug] : undefined;

  if (!industry) {
    return {
      notFound: true,
    };
  }

  const [announcementBanner, industrySolutionPage, stackedLogos, destinations, referenceBlogPosts] = await Promise.all([
    getAnnouncementBanner(),
    industry.solutionPageSlug ? getPage(industry.solutionPageSlug) : null,
    getStackedLogos(),
    getSimpleIntegrations("destination"),
    getFeaturedBlogs(["composable-cdp", "cdp-rfp-template", "checkmate-in-marketing"]),
  ]);

  return {
    props: {
      industrySlug: slug!,
      announcementBanner,
      industrySolutionPage: industrySolutionPage ?? null,
      stackedLogos,
      destinations,
      referenceBlogPosts,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: string[] = Object.keys(cdpIndustryVariants);

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default CdpIndustryPageTemplate;
